/**
 * Where the production desktop app is downloaded from, and which installer
 * fits this computer.
 *
 * The installers are the latest production release (`releases/latest/`). The
 * Mac build is Apple silicon only, so an Intel Mac is told so instead of being
 * handed a disk image that will not open. Every other channel installs
 * "Dooglie Dev" from `/downloads`; that page reads its own manifest.
 */

export const DESKTOP_DOWNLOAD_URLS = {
  windows:
    'https://dooglie-downloads.s3.eu-west-2.amazonaws.com/dooglie-desktop/releases/latest/dooglie-desktop.exe',
  mac: 'https://dooglie-downloads.s3.eu-west-2.amazonaws.com/dooglie-desktop/releases/latest/dooglie-desktop.dmg',
} as const;

export type DesktopDownload = {
  url: string;
  label: string;
  /** An Intel Mac: the Mac build needs Apple silicon, so do not offer it. */
  intelMac: boolean;
};

/** The slice of `navigator` read here, so tests can pass their own. */
export type NavigatorLike = {
  userAgent: string;
  userAgentData?: {
    platform?: string;
    getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }>;
  };
};

/**
 * The installer for this computer, or null when there is none (Linux,
 * ChromeOS, phones).
 *
 * The architecture comes from User-Agent Client Hints, which Chrome has and
 * Safari and Firefox do not. Without it a Mac is assumed to be Apple silicon:
 * every Mac browser reports "Intel" in its user agent string, so that string
 * cannot tell them apart.
 */
export async function pickDesktopDownload(
  nav: NavigatorLike = navigator as NavigatorLike,
): Promise<DesktopDownload | null> {
  const platform = (nav.userAgentData?.platform || nav.userAgent).toLowerCase();

  if (platform.includes('win')) {
    return { url: DESKTOP_DOWNLOAD_URLS.windows, label: 'Windows installer (.exe)', intelMac: false };
  }
  if (!platform.includes('mac')) return null;

  let architecture: string | undefined;
  try {
    architecture = (await nav.userAgentData?.getHighEntropyValues?.(['architecture']))
      ?.architecture;
  } catch {
    architecture = undefined;
  }
  return {
    url: DESKTOP_DOWNLOAD_URLS.mac,
    label: 'Mac installer (.dmg)',
    intelMac: architecture === 'x86',
  };
}
